import { fetchCustomers } from "../../../api/customer";
import { fetchProjects } from "../../../api/project";
import { buildProjectsIndexParams } from "../../../utils/projectsIndexParams";
import { fetchCases, searchSlashModules } from "../../../api/case";
import { fetchReports } from "../../../api/service/report";
import { getHseDocuments } from "../../../api/hse/documents";
import { getEntityRoute, parseWorkspaceQuery } from "./intentParser";

const LIST_FIELDS = {
  customer: "id,displayTitle,name,customer_number,email,mobile,updated_at,created_at",
  report:
    "id,displayTitle,report_number,status,approval_status,customer,project,updated_at,created_at",
  project:
    "id,displayTitle,name,project_number,active,finished,customer,updated_at,created_at",
  case:
    "id,displayTitle,case_number,title,status,customer,project,updated_at,created_at",
};

export function getExampleQueries(t) {
  return [
    t("globalSearch.suggestion.latest_project"),
    t("globalSearch.suggestion.latest_report"),
    t("globalSearch.suggestion.find_customer"),
    t("globalSearch.suggestion.find_project"),
    t("globalSearch.suggestion.find_hse_document"),
    t("globalSearch.suggestion.report_status"),
  ];
}

function entityLabel(entity, t) {
  if (entity === "case") return t("Case");
  if (entity === "hse_document") return t("globalSearch.entity.hse_document");
  return t(
    entity.charAt(0).toUpperCase() + entity.slice(1)
  );
}

function customerFilter(customerId) {
  return { 0: { name: "customer", value: String(customerId) } };
}

function toHseDocumentCard(row) {
  const meta = [];
  if (row.document_number) meta.push(row.document_number);
  if (row.document_type_display) meta.push(row.document_type_display);
  if (row.status_display || row.status) meta.push(row.status_display || String(row.status));

  return {
    id: row.id,
    module: "hse_document",
    title: String(row.title || row.document_number || `#${row.id}`),
    meta: meta.filter(Boolean),
    href: getEntityRoute("hse_document", row.id),
    object: row,
  };
}

async function searchHseDocuments(query, perPage = 5) {
  const filters = query ? { search: query } : {};
  const res = await getHseDocuments(filters);
  const docs = res?.documents || [];
  return docs.slice(0, perPage).map((row) => toHseDocumentCard(row));
}

function toResultCard(module, row) {
  const id = row.id;
  const title =
    row.displayTitle ??
    row.name ??
    row.title ??
    row.report_number ??
    (row.case_number ? `SAK-${row.case_number}` : null) ??
    `#${id}`;

  const meta = [];
  if (module === "report" && row.report_number) meta.push(`#${row.report_number}`);
  if (module === "case" && row.case_number) meta.push(`SAK-${row.case_number}`);
  if (module === "project" && row.project_number) meta.push(row.project_number);
  if (row.customer?.displayTitle || row.customer?.name) {
    meta.push(row.customer.displayTitle || row.customer.name);
  }
  if (row.status) meta.push(String(row.status));

  return {
    id,
    module,
    title: String(title),
    meta: meta.filter(Boolean),
    href: getEntityRoute(module, id),
    object: row,
  };
}

async function searchCustomersByName(name, perPage = 5) {
  const res = await fetchCustomers({
    query: name,
    page: 1,
    per_page: perPage,
    sortBy: "updated_at",
    sort: "desc",
    fields: LIST_FIELDS.customer,
  });
  return res?.data || [];
}

async function searchEntity(entity, query, options = {}) {
  const { customerId, perPage = 5, sortBy = "updated_at" } = options;
  const base = {
    query,
    page: 1,
    per_page: perPage,
    sortBy,
    sort: "desc",
    fields: LIST_FIELDS[entity],
  };

  if (customerId) {
    base.filters = customerFilter(customerId);
  }

  let res;
  switch (entity) {
    case "customer":
      res = await fetchCustomers(base);
      break;
    case "report":
      res = await fetchReports(base);
      break;
    case "project":
      res = await fetchProjects(
        buildProjectsIndexParams(base, { activeOnly: false })
      );
      break;
    case "case":
      res = await fetchCases(base);
      break;
    case "hse_document":
      return searchHseDocuments(query, perPage);
    default:
      return [];
  }

  const rows = res?.data || [];
  return rows.map((row) => toResultCard(entity, row));
}

function formatProjectStatus(row, t) {
  if (row.finished) return t("globalSearch.status.project.finished");
  if (row.active === false) return t("globalSearch.status.project.inactive");
  return t("globalSearch.status.project.active");
}

function formatReportStatus(row, t) {
  const parts = [];
  if (row.status) parts.push(String(row.status));
  if (row.approval_status) parts.push(String(row.approval_status));
  return parts.length ? parts.join(" · ") : t("globalSearch.status.unknown");
}

function formatCaseStatus(row) {
  return row.status ? String(row.status) : null;
}

function formatHseDocumentStatus(row) {
  return row.status_display || (row.status ? String(row.status) : null);
}

async function executeSearch(parsed, t) {
  switch (parsed.intent) {
    case "help":
      return {
        results: [],
        statusLine: null,
        suggestions: getExampleQueries(t),
      };

    case "latest_entity": {
      const results = await searchEntity(parsed.entity, "", {
        perPage: 1,
        sortBy: "created_at",
      });

      if (!results.length) {
        return {
          results: [],
          statusLine: t("globalSearch.no_latest_entity", {
            entity: entityLabel(parsed.entity, t),
          }),
          suggestions: [],
        };
      }

      return {
        results,
        statusLine: t("globalSearch.latest_entity", {
          entity: entityLabel(parsed.entity, t),
        }),
        suggestions: [],
      };
    }

    case "latest_for_customer": {
      const customers = await searchCustomersByName(parsed.customerName, 5);
      if (!customers.length) {
        return {
          results: [],
          statusLine: t("globalSearch.no_customer", {
            name: parsed.customerName,
          }),
          suggestions: [],
        };
      }
      if (customers.length > 1) {
        return {
          results: customers.map((c) => toResultCard("customer", c)),
          statusLine: t("globalSearch.multiple_customers", {
            name: parsed.customerName,
          }),
          suggestions: [],
        };
      }

      const customer = customers[0];
      const results = await searchEntity(parsed.entity, "", {
        customerId: customer.id,
        perPage: 5,
      });

      if (!results.length) {
        return {
          results: [],
          statusLine: t("globalSearch.no_results_for_customer", {
            entity: entityLabel(parsed.entity, t),
            customer: customer.displayTitle || customer.name,
          }),
          suggestions: [],
        };
      }

      return {
        results,
        statusLine: t("globalSearch.latest_for_customer", {
          entity: entityLabel(parsed.entity, t),
          customer: customer.displayTitle || customer.name,
        }),
        suggestions: [],
      };
    }

    case "status_lookup": {
      const results = await searchEntity(parsed.entity, parsed.query, {
        perPage: 3,
      });
      if (!results.length) {
        return {
          results: [],
          statusLine: t("globalSearch.no_results", { query: parsed.query }),
          suggestions: [],
        };
      }

      const primary = results[0];
      const row = primary.object;
      let statusText = t("globalSearch.status.unknown");

      if (parsed.entity === "report") statusText = formatReportStatus(row, t);
      else if (parsed.entity === "project") statusText = formatProjectStatus(row, t);
      else if (parsed.entity === "case") statusText = formatCaseStatus(row) || statusText;
      else if (parsed.entity === "hse_document") {
        statusText = formatHseDocumentStatus(row) || statusText;
      }
      else if (parsed.entity === "customer") {
        statusText = row.active === false
          ? t("globalSearch.status.customer.inactive")
          : t("globalSearch.status.customer.active");
      }

      return {
        results,
        statusLine: t("globalSearch.status_result", {
          title: primary.title,
          status: statusText,
        }),
        suggestions: [],
      };
    }

    case "find_entity": {
      const results = await searchEntity(parsed.entity, parsed.query, {
        perPage: 8,
      });
      if (!results.length) {
        return {
          results: [],
          statusLine: t("globalSearch.no_results", { query: parsed.query }),
          suggestions: [],
        };
      }
      return {
        results,
        statusLine: t("globalSearch.found_count", { count: results.length }),
        suggestions: [],
      };
    }

    case "generic_search":
    default: {
      const q = parsed.query || "";
      if (!q) {
        return {
          results: [],
          statusLine: null,
          suggestions: getExampleQueries(t),
        };
      }

      const [slashResults, caseResults, hseDocumentResults] = await Promise.all([
        searchSlashModules(q),
        searchEntity("case", q, { perPage: 5 }).catch(() => []),
        searchHseDocuments(q, 5).catch(() => []),
      ]);

      const combined = [
        ...slashResults.map((r) => ({
          id: r.id,
          module: r.module,
          title: String(r.displayTitle),
          meta: [t(r.labelKey)],
          href: getEntityRoute(r.module, r.id),
          object: r.object,
        })),
        ...caseResults,
        ...hseDocumentResults,
      ];

      const seen = new Set();
      const deduped = combined.filter((item) => {
        const key = `${item.module}:${item.id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      if (!deduped.length) {
        return {
          results: [],
          statusLine: t("globalSearch.no_results", { query: q }),
          suggestions: [],
        };
      }

      return {
        results: deduped.slice(0, 12),
        statusLine: t("globalSearch.found_count", { count: deduped.length }),
        suggestions: [],
      };
    }
  }
}

export async function searchGlobal(rawQuery, t) {
  const parsed = parseWorkspaceQuery(rawQuery);
  return executeSearch(parsed, t);
}
